import { useState, useMemo, useEffect, useContext } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { Watch } from 'react-loader-spinner';

import EasePicker, { EasePickOptions, RangePlugin, LockPlugin, DateTime } from 'react-easepick';
import useWindowDimensions from '../hooks/useWindowDimensions';
import CalendarAvailabilityContext from '../context/CalendarAvailabilityContext';

const isProd = process.env.REACT_APP_VERCEL_ENV === 'production';

type StartEndDate = {
  start: String | null;
  end: String | null;
};

type Props = {
  dateRange: StartEndDate | undefined;
  onSelectDate: (dateRange: StartEndDate) => void;
  columns: Number;
  pickerInstance: any;
  setPickerInstance: any;
  numCalendars?: number;
  mobileModal?: boolean;
};

const DEFAULT_MIN_DAYS = 2;

// let pickerInstances: any[] = [];

function DatePicker({
  dateRange,
  onSelectDate = () => {},
  columns,
  pickerInstance,
  setPickerInstance,
  numCalendars,
  mobileModal = false
}: Props) {
  const { height, width } = useWindowDimensions();

  const { isCalendarPending, isCalendarError, calendarData, calendarError } = useContext(
    CalendarAvailabilityContext
  );

  useEffect(() => {
    if (pickerInstance && pickerInstance.getStartDate) {
      if (
        moment(pickerInstance.getStartDate()).format('MM-DD-YYYY') !==
        /* @ts-ignore */
        moment(dateRange!.start).format('MM-DD-YYYY')
      ) {
        pickerInstance.setStartDate(dateRange!.start);
        pickerInstance.gotoDate(dateRange!.start);
      }
      if (pickerInstance.getEndDate() !== dateRange!.end) {
        pickerInstance.setEndDate(dateRange!.end);
      }
    }
  }, [dateRange]);

  const options = useMemo(() => {
    if (isCalendarPending || isCalendarError) {
      return;
    }
    return {
      //   element: document.getElementById(elementId),
      doc: window.document,
      css: [
        'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
        isProd && 'https://react-datepicker-ts-orcin.vercel.app/DatePicker.css', // prod
        !isProd && '/DatePicker.css' // dev
      ],
      firstDay: 0,
      grid: columns,
      calendars: numCalendars,
      inline: true,
      header: false,
      plugins: [RangePlugin, LockPlugin],
      RangePlugin: {
        tooltipNumber(num: any) {
          return num - 1;
        },
        locale: {
          one: 'night',
          other: 'nights'
        },
        strict: true
      },
      LockPlugin: {
        minDate: new Date(),
        minDays: DEFAULT_MIN_DAYS,
        maxDays: 8,
        inseparable: true,
        selectForward: true,
        filter(date: Date, picked: Date[]) {
          const formattedDate = moment(date).format();
          const foundMatch = allDates.find((item, index) => {
            if (formattedDate == item.formattedDate) {
              if (item.isPossibleStartDate || item.isAvailableForCheckout) {
                return false;
              }
              if (!item.isAvailable && !item.isAvailableForCheckout) {
                return true;
              }

              if (!item.isAvailable && item.isAvailableForCheckout && picked.length <= 0) {
                // show checkout dates when selected
                return true;
              }
            }

            return false;
          });
          return foundMatch;
        }
      },
      setup(picker: any) {
        const lockPlugin = picker.PluginManager.getInstance('LockPlugin');
        // handle select of date
        picker.on('render', (evt: any) => {
          if (evt.detail.view === 'Container') {
            evt.target.classList.add(`columns-${columns}`);
            if (mobileModal) {
              evt.target.classList.add(`in-mobile-modal`);
            }
          }
        });
        // add price to day element
        picker.on('view', (evt: any) => {
          const { view, date, target } = evt.detail;
          const formattedDate = date ? moment(date).format('YYYY-MM-DD') : null;
          if (
            view === 'CalendarDay' &&
            formattedDate &&
            dateHashMap[formattedDate] &&
            (dateHashMap[formattedDate].isAvailable ||
              dateHashMap[formattedDate].isPossibleStartDate ||
              dateHashMap[formattedDate].isAvailableForCheckout)
          ) {
            const matchedDate = allDates.find((item) => {
              return formattedDate === item.yyyymmdd;
            });

            if (matchedDate && !matchedDate.isPossibleStartDate) {
              target.classList.add(`not-possible-start-date`);
            }

            if (dateHashMap[formattedDate].isAvailable) {
              const span = target.querySelector('.day-price') || document.createElement('span');
              span.className = 'day-price';
              span.innerHTML = `$ ${dateHashMap[formattedDate].price}`;
              // TEMP: HIDE PRICE FOR NOW
              target.append(span);
            }
          }
        });

        // handle select of date
        picker.on('select', (e: any) => {
          const { start, end } = e.detail;
          e.target.classList.remove('selecting-range');

          lockPlugin.options.minDays = DEFAULT_MIN_DAYS;
          onSelectDate({ start, end });
        });

        picker.on('preselect', (e: any) => {
          const { start, end } = e.detail;

          const formattedStartDate = moment(start).format('YYYY-MM-DD');
          const matchedDate = allDates.find((item) => {
            return formattedStartDate === item.yyyymmdd;
          });

          e.target.classList.add('selecting-range');

          lockPlugin.options.minDays = matchedDate.minimumStay + 1;

          // refresh layout
          picker.renderAll();
        });

        setPickerInstance(picker);
      }
    };
  }, [calendarData, numCalendars]);

  if (isCalendarPending) {
    return (
      <LoaderWrapper>
        <Watch
          visible={true}
          height="80"
          width="80"
          radius="48"
          color="#ddd"
          ariaLabel="watch-loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
      </LoaderWrapper>
    );
  }

  if (isCalendarError) {
    return <span>Error: {calendarError.message}</span>;
  }

  const dateHashMap = calendarData.result;

  const allDates = Object.keys(calendarData.result).map((key) => {
    const item = calendarData.result[key];
    const keyDayBefore = moment(key).subtract(1, 'day').format('YYYY-MM-DD');
    const dayBefore = calendarData.result[keyDayBefore];

    // @ts-ignore
    item.dateObj = new Date(item.date, 'YYYY-MM-DD');
    item.formattedDate = moment(item.date).format();
    item.yyyymmdd = moment(item.date).format('YYYY-MM-DD');

    if (dayBefore && !item.isAvailable && dayBefore.isAvailable) {
      item.isAvailableForCheckout = 1;
    } else {
      item.isAvailableForCheckout = 0;
    }
    const minCheckoutDateKey = moment(key).add(item.minimumStay, 'day').format('YYYY-MM-DD');
    const minCheckoutDate = calendarData.result[minCheckoutDateKey];
    item.isPossibleStartDate = minCheckoutDate
      ? item.isAvailable && (minCheckoutDate.isAvailable || minCheckoutDate.isAvailableForCheckout)
      : false;

    return item;
  });

  return (
    <DatePickerWrapper columns={columns} isProd={isProd}>
      <EasePicker date={dateRange as any} options={options as any} />
    </DatePickerWrapper>
  );
}

const LoaderWrapper = styled.div`
  display: flex;
  height: 290px;
  justify-content: center;
  align-items: center;
`;

const DatePickerWrapper = styled.div<{ columns: Number; isProd: Boolean }>`
  min-height: 290px;
  > input {
    display: none;
  }
  width: 100%;

  ${({ columns, isProd }) =>
    columns === 1
      ? css`
          ${!isProd && 'max-width: 412px;'}
        `
      : ''}

  ${({ columns, isProd }) =>
    columns === 2
      ? css`
          ${!isProd && 'max-width: 720px;'}
          ${!isProd && 'margin: 0 auto;'}
        `
      : ''}
  @media screen and (max-width: 480px) {
    width: 100%;
    max-width: 380px;
    .easepick-wrapper {
      width: calc(100% - 40px);
    }
  }
`;

export default DatePicker;
